import { useState, useRef, useEffect, useCallback } from 'react';
import { io } from 'socket.io-client';
import { notifications } from '@mantine/notifications';
import { SOCKET_URL, SOCKET_PATH, SOCKET_EVENTS, GAME_DEFAULTS } from '../config/constants';

/**
 * Custom hook to manage socket.io events and game state.
 * Extracts the complex socket logic and listeners away from the context provider.
 * 
 * @param {Object} setters - State setters for UI-related state managed by the context.
 * @param {Function} setters.setSetupStep - Updates the current step in the setup wizard.
 * @param {Function} setters.setLoading - Updates the loading state.
 * @param {Function} setters.setErrors - Updates the form error state.
 * @returns {Object} An object containing game state variables and socket control functions.
 */
export const useGameSocket = ({ setSetupStep, setLoading, setErrors }) => {
    const [timeline, setTimeline] = useState([]);
    const [botCount, setBotCount] = useState(0);
    const [gameState, setGameState] = useState('lobby');
    const [timeLeft, setTimeLeft] = useState(0);
    const [totalTime, setTotalTime] = useState(GAME_DEFAULTS.QUESTION_TIME_MS);
    const [answerMode, setAnswerMode] = useState('random');
    const [podium, setPodium] = useState([]);
    const [questionNumber, setQuestionNumber] = useState(0);
    const [socket, setSocket] = useState(null);

    const socketRef = useRef(null);
    const answerModeRef = useRef('random');

    // Keep the ref in sync with the state for use in socket listeners
    useEffect(() => { answerModeRef.current = answerMode; }, [answerMode]);

    /**
     * Records a new event into the game timeline with a timestamp.
     * @param {string} title - The title of the event.
     * @param {string} type - The severity/type of event (success, error, warning).
     * @param {string} message - A description of the event.
     * @param {any} data - Optional extra data (e.g., answer index).
     */
    const recordEvent = useCallback((title, type, message = "", data = null) => {
        setTimeline((prev) => {
            if (prev.length > 0 && prev[0].title === title && prev[0].message === message) return prev;
            return [{
                title, message, type, data,
                time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
            }, ...prev];
        });
    }, []);

    /**
     * Effect hook that manages the countdown timer when the game state is 'question'.
     */
    useEffect(() => {
        let interval;
        if (gameState === 'question' && timeLeft > 0) {
            interval = setInterval(() => setTimeLeft((p) => Math.max(0, p - 1000)), 1000);
        }
        return () => clearInterval(interval);
    }, [gameState, timeLeft]);

    /**
     * Initializes the socket connection if it doesn't already exist.
     */
    const connectSocket = useCallback(() => {
        if (socketRef.current) return;

        console.log("Initializing Socket...");
        const newSocket = io(SOCKET_URL, { path: SOCKET_PATH });
        socketRef.current = newSocket;
        setSocket(newSocket);
    }, []);

    /**
     * Disconnects the socket and resets all related game states.
     */
    const disconnectBots = useCallback(() => {
        socketRef.current?.emit(SOCKET_EVENTS.USER_DISCONNECT);
        socketRef.current?.disconnect();
        socketRef.current = null;
        setSocket(null);
        
        setBotCount(0);
        setGameState('lobby');
        setTimeline([]);
        setSetupStep(0);
        setLoading(false);
        setPodium([]);
    }, [setSetupStep, setLoading]);

    /**
     * Sends a manual answer command to the server.
     * @param {any} val - The answer index or value.
     */
    const sendAnswer = useCallback((val) => {
        socketRef.current?.emit(SOCKET_EVENTS.USER_ANSWER, { answer: val });
        
        // Record as an event if it's a specific answer
        if (typeof val === 'number') {
            const symbols = ['Triangle', 'Diamond', 'Circle', 'Square'];
            recordEvent(`Answer Sent (Q${questionNumber})`, "answer", `Manual command: ${symbols[val]}`, val);
        }
    }, [recordEvent, questionNumber]);

    /**
     * Effect hook that manages all socket.io event listeners.
     * Listens for bot join success, podium updates, and game time events.
     */
    useEffect(() => {
        if (!socket) return;

        socket.on(SOCKET_EVENTS.CONNECT, () => {
            console.log("Connected with Real ID:", socket.id);
        });

        socket.on(SOCKET_EVENTS.DISCONNECT, (reason) => {
            if (reason === "io server disconnect" || reason === "transport close") {
                setGameState('ended');
                setBotCount(0);
                setTimeLeft(0);
                recordEvent("Connection Lost", "error", "Server closed the connection.");
                socketRef.current = null;
                setSocket(null);
            }
        });

        socket.on(SOCKET_EVENTS.BOT_JOIN_SUCCESS, () => {
            setBotCount((c) => c + 1);
            setSetupStep((prev) => (prev !== 3 ? 3 : prev));
            setLoading(false);
        });

        socket.on(SOCKET_EVENTS.BOT_JOIN_FAIL, (data) => recordEvent("Join Failed", "error", data.message));

        socket.on(SOCKET_EVENTS.BOT_SPAWN_ERROR, (data) => {
            setLoading(false);
            setSetupStep(0);
            const message = data.description || "Error initializing bots";
            setErrors({ pin: message });
            notifications.show({ title: 'Spawn Error', message, color: 'red' });
            socket.disconnect();
            socketRef.current = null;
            setSocket(null);
        });

        socket.on(SOCKET_EVENTS.BOT_DISCONNECTED, (data) => {
            setBotCount((c) => Math.max(0, c - 1));
            recordEvent("Bot Disconnected", "error", data.message);
        });

        socket.on(SOCKET_EVENTS.QUIZ_STARTED, () => {
            setGameState('playing');
            setQuestionNumber(0);
            recordEvent("Quiz Started", "success", "Good luck!");
        });

        socket.on(SOCKET_EVENTS.QUESTION_READY, () => {
            setGameState('playing');
            setTimeLeft(0);
        });

        socket.on(SOCKET_EVENTS.QUESTION_START, (data) => {
            setGameState('question');
            setQuestionNumber((q) => q + 1);
            setTotalTime(data.time || GAME_DEFAULTS.QUESTION_TIME_MS);
            setTimeLeft(data.time || GAME_DEFAULTS.QUESTION_TIME_MS);

            if (answerModeRef.current === 'random') {
                const delay = Math.random() * (data.time * 0.9);
                setTimeout(() => socketRef.current?.emit(SOCKET_EVENTS.USER_ANSWER, { answer: "random" }), delay);
            }
        });

        socket.on(SOCKET_EVENTS.QUESTION_ENDED, () => {
            setGameState('playing');
            setTimeLeft(0);
        });

        socket.on(SOCKET_EVENTS.PODIUM, (data) => {
            setGameState('ended');
            setTimeLeft(0);
            setPodium((prev) => {
                if (data.rank > 3) return prev;
                if (prev.some(p => p.botName === data.botName)) return prev;
                const newList = [...prev, data].sort((a, b) => a.rank - b.rank);
                recordEvent("Podium Reached!", "success", `${data.botName} placed Rank #${data.rank}!`);
                return newList;
            });
        });

        socket.on(SOCKET_EVENTS.QUIZ_ENDED, () => {
            setGameState((prev) => {
                if (prev !== 'ended') {
                    recordEvent("Session Finished", "warning", "The host ended the game.");
                    return 'ended';
                }
                return prev;
            });
            setTimeLeft(0);
        });

        return () => {
            socket.off(SOCKET_EVENTS.CONNECT);
            socket.off(SOCKET_EVENTS.DISCONNECT);
            socket.off(SOCKET_EVENTS.BOT_JOIN_SUCCESS);
            socket.off(SOCKET_EVENTS.BOT_JOIN_FAIL);
            socket.off(SOCKET_EVENTS.BOT_SPAWN_ERROR);
            socket.off(SOCKET_EVENTS.BOT_DISCONNECTED);
            socket.off(SOCKET_EVENTS.QUIZ_STARTED);
            socket.off(SOCKET_EVENTS.QUESTION_READY);
            socket.off(SOCKET_EVENTS.QUESTION_START);
            socket.off(SOCKET_EVENTS.QUESTION_ENDED);
            socket.off(SOCKET_EVENTS.PODIUM);
            socket.off(SOCKET_EVENTS.QUIZ_ENDED);
        };
    }, [socket, setSetupStep, setLoading, setErrors, recordEvent]);

    return {
        gameState,
        botCount,
        timeline,
        timeLeft,
        totalTime,
        answerMode,
        setAnswerMode,
        podium,
        socketRef,
        connectSocket,
        disconnectBots,
        sendAnswer,
        recordEvent
    };
};
